import React, { PropsWithChildren, ReactElement, useMemo } from 'react'

import { Button, Grid, Typography } from '@barracuda-internal/bds-core'
import { HelpOutline } from '@barracuda-internal/bds-core/dist/Icons/Core'
import styles from 'components/libs/layout/pages/pagesLayoutStyles'
import { useFormatMessage } from 'lib/localization'
import Help from 'components/pages/support/help/Help'
import { HelpComponentProps } from 'types/Help'
import { GROUP_TITLE, TOPICS } from 'components/pages/support/config'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'

const BASE_I18N_KEY = 'ess.layout'

export type PageTitleProps = {
  title: string | ReactElement
  onOpenSupport?: () => void
  onCancel?: () => void
  cancelButtonText?: string
  onSubmit?: () => void
  submitButtonText?: string
  submitInProgress?: boolean
  isDisabledSubmit?: boolean
  help?: {
    content: (props: HelpComponentProps) => JSX.Element
    onClose: () => void
    title: TOPICS
    group: GROUP_TITLE
    isHelpOpen: boolean
  }
  onReset?: () => void
  isResetPending?: boolean
  closeWarningDialog?: () => void
}

export const PageTitle = ({
  title,
  onOpenSupport,
  onCancel,
  cancelButtonText,
  onSubmit,
  submitButtonText,
  submitInProgress,
  children,
  isDisabledSubmit,
  help,
  onReset,
  isResetPending,
  closeWarningDialog
}: PropsWithChildren<PageTitleProps>) => {
  const isReadonly = useIsReadonly()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const classes = styles()

  const isDisabledCancel = useMemo(() => !!onReset && isDisabledSubmit, [isDisabledSubmit, onReset])

  return useMemo(
    () => (
      <Grid className={classes.pageTitle} alignItems="center" container direction="row">
        {isResetPending && (
          <WarningDialog
            title={formatMessage('unsaved_dialog.title')}
            description={formatMessage('unsaved_dialog.text')}
            onCancel={() => closeWarningDialog?.()}
            onConfirm={() => {
              onReset?.()
              closeWarningDialog?.()
            }}
          />
        )}
        <Grid xs item>
          <Grid container direction="row">
            <Typography variant="h1">{title}</Typography>
            {onOpenSupport && (
              <HelpOutline data-testid="on-open-support" onClick={onOpenSupport} className={classes.helpIcon} />
            )}
          </Grid>
        </Grid>
        {(onCancel || onSubmit || children) && (
          <Grid xs item>
            <Grid container direction="row" justifyContent="flex-end">
              {onCancel && (
                <Button className={classes.cancelButton} onClick={onCancel} disabled={isReadonly || isDisabledCancel}>
                  {cancelButtonText || formatMessage('cancel')}
                </Button>
              )}
              {onSubmit && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={onSubmit}
                  isLoading={submitInProgress}
                  disabled={isReadonly || isDisabledSubmit}
                >
                  {submitButtonText || formatMessage('save_changes')}
                </Button>
              )}
              {children}
            </Grid>
          </Grid>
        )}
        <Grid>
          {help?.isHelpOpen && (
            <Help
              modalComponentInfo={{
                content: help.content,
                onClose: help.onClose,
                title: help.title,
                groupTitle: help.group
              }}
            />
          )}
        </Grid>
      </Grid>
    ),
    [
      classes.pageTitle,
      classes.helpIcon,
      classes.cancelButton,
      isResetPending,
      formatMessage,
      title,
      onOpenSupport,
      onCancel,
      onSubmit,
      children,
      isReadonly,
      isDisabledCancel,
      cancelButtonText,
      submitInProgress,
      isDisabledSubmit,
      submitButtonText,
      help,
      closeWarningDialog,
      onReset
    ]
  )
}
